import React, { useState } from 'react';
import { Button, ButtonBar, Dialog } from '@ygtechnologies/rubus-ui';
import { MarkdownDisplay, classnames } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { ConfiguratorPresetDto, ConfiguratorPresetGroupDto } from '@/services/configurator';
import { StyleSelectionCard } from './StyleSelectionCard';
import theme from './StyleSelectionModal.module.scss';

export interface StyleSelectionModalProps {
    /**
     * The style group(s) to walk through. A single group behaves like a normal dialog (accept just
     * closes); multiple groups turn it into a sequential wizard — accepting one advances to the next,
     * keeping the dialog open and only swapping its content.
     */
    groups: ConfiguratorPresetGroupDto[];
    onClose: () => void;
}

/**
 * Style-selection dialog. Selecting a style (in a card) picks it immediately and leaves the dialog
 * open; a separate "accept / proceed" footer button either closes the dialog (single group) or
 * advances to the next group (sequential walkthrough). A forced group can't be proceeded past until
 * one of its styles is picked.
 */
export const StyleSelectionModal: React.FC<StyleSelectionModalProps> = ({ groups, onClose }) => {
    const [index, setIndex] = useState(0);
    // Optimistic pick per group id: styleId while picked, null while explicitly unpicked, absent when
    // the user hasn't touched the group (fall back to the server's `isPicked`). The backend pick round
    // trip (and the showroom /calculate) has no optimistic patch for styles, so without this the
    // selection indicator wouldn't move until the server responds.
    const [optimistic, setOptimistic] = useState<Record<number, number | null>>({});

    // Clamp so a shrinking list (a group resolved away) can't leave us out of bounds.
    const safeIndex = Math.min(index, groups.length - 1);
    const group = groups[safeIndex];

    // Size the dialog to the widest group in the walkthrough so it doesn't jump between steps:
    // 1 style -> sm, 2 -> md (two-up), 3+ -> lg (three-up).
    const maxStyles = Math.max(1, ...groups.map((g) => g.styles.length));
    const columns = Math.min(maxStyles, 3);
    const contentWidth = columns >= 3 ? 'lg' : columns === 2 ? 'md' : 'sm';

    if (!group) return null;

    const isStyleSelected = (g: ConfiguratorPresetGroupDto, style: ConfiguratorPresetDto) => {
        const pick = optimistic[g.id];
        return pick === undefined ? style.isPicked : pick === style.id;
    };

    const isFirst = safeIndex === 0;
    const isLast = safeIndex >= groups.length - 1;
    const anyPicked = group.styles.some((s) => isStyleSelected(group, s));
    // The user must make a selection before moving on when a pick is expected: either the group is
    // forced, or this is a multi-group walkthrough (each step should be an explicit choice). A single
    // optional group can still be dismissed via the dialog's close button.
    const blockProceed = !anyPicked && (group.forceSelection || groups.length > 1);

    const handleProceed = () => {
        if (isLast) onClose();
        else setIndex(safeIndex + 1);
    };

    return (
        <Dialog
            open
            heightLimit="window"
            onOpenChange={(open) => {
                if (!open) onClose();
            }}>
            <Dialog.Content contentWidth={contentWidth} className={theme.content}>
                <Dialog.Header>
                    <div className={theme.header}>
                        <span>{group.name}</span>
                        {groups.length > 1 && (
                            <span className={theme.stepCount}>
                                {safeIndex + 1} / {groups.length}
                            </span>
                        )}
                    </div>
                </Dialog.Header>
                {/* Only this middle region scrolls; the header above and the footer below stay fixed. */}
                <div className={theme.body}>
                    {group.description && (
                        <MarkdownDisplay className={theme.description} markdown={group.description} />
                    )}
                    <div className={classnames(theme.grid, theme[`cols${columns}`])}>
                        {group.styles.map((style) => (
                            <StyleSelectionCard
                                key={style.id}
                                style={style}
                                styleGroup={group}
                                isSelected={isStyleSelected(group, style)}
                                onPicked={(styleId) => setOptimistic((prev) => ({ ...prev, [group.id]: styleId }))}
                            />
                        ))}
                    </div>
                </div>
                <ButtonBar margins="none" className={theme.footer}>
                    {!isFirst && (
                        <Button variant="tertiary" onClick={() => setIndex(safeIndex - 1)}>
                            <Trans>Back</Trans>
                        </Button>
                    )}
                    <ButtonBar.Right>
                        <Button variant="primary" onClick={handleProceed} disabled={blockProceed}>
                            {isLast ? <Trans>Confirm selection</Trans> : <Trans>Continue</Trans>}
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </Dialog.Content>
        </Dialog>
    );
};
